import { useId, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

type Logo = { url: string; label?: string | null };

export function LogoStack({
  primaryLogoUrl,
  primaryLabel,
  fallbackText,
  logos,
  size = 64,
  collapsedStep = 18,
  expandedGap = 10,
  className,
}: {
  primaryLogoUrl?: string | null;
  primaryLabel?: string | null;
  fallbackText: string;
  logos: Logo[];
  size?: number;
  collapsedStep?: number; // px overlap step (smaller = more stacked)
  expandedGap?: number; // px gap when expanded
  className?: string;
}) {
  const uid = useId();
  const [expanded, setExpanded] = useState(false);

  const items = useMemo(() => {
    const out: Array<{ url?: string; label: string; kind: "image" | "fallback" }> = [];
    const primaryUrl = String(primaryLogoUrl || "").trim();
    if (primaryUrl) {
      out.push({ url: primaryUrl, label: String(primaryLabel || "Main logo"), kind: "image" });
    } else {
      out.push({ label: fallbackText, kind: "fallback" });
    }
    for (const l of logos || []) {
      const u = String(l?.url || "").trim();
      if (!u) continue;
      if (primaryUrl && u === primaryUrl) continue;
      out.push({ url: u, label: String(l?.label || "Logo"), kind: "image" });
    }
    return out.slice(0, 7);
  }, [fallbackText, logos, primaryLabel, primaryLogoUrl]);

  const expandedStep = size + expandedGap;

  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={`${uid}-logos`}
      onClick={() => setExpanded((v) => !v)}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      className={cn("relative mx-auto select-none", className)}
      style={{ height: `${size}px`, width: `${size + (items.length - 1) * collapsedStep}px` }}
      title={items.length > 1 ? "Tap to expand logos" : undefined}
    >
      <span id={`${uid}-logos`} className="sr-only">
        Logos
      </span>

      {items.map((it, idx) => {
        const tx = idx * (expanded ? expandedStep : collapsedStep);
        const z = 50 - idx;
        return (
          <span
            key={`${uid}-${idx}`}
            className={cn(
              "absolute top-0 left-0 rounded-full overflow-hidden border bg-muted",
              "transition-transform duration-300 ease-out will-change-transform",
            )}
            style={{
              width: `${size}px`,
              height: `${size}px`,
              transform: `translateX(${tx}px)`,
              zIndex: z,
            }}
          >
            {it.kind === "image" ? (
              <img
                src={String(it.url)}
                alt={it.label}
                className="h-full w-full object-cover"
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center bg-primary text-primary-foreground text-2xl font-bold">
                {fallbackText}
              </span>
            )}
          </span>
        );
      })}

      {/* Expand hint */}
      {items.length > 1 ? (
        <span
          className={cn(
            "absolute -bottom-5 left-1/2 -translate-x-1/2 text-[11px] text-muted-foreground transition-opacity",
            expanded ? "opacity-0" : "opacity-100",
          )}
        >
          Tap logos
        </span>
      ) : null}
    </button>
  );
}

